import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import {
    Box, Card, CardActionArea, CardContent, Collapse,
    FormControl, FormControlLabel, InputLabel, MenuItem, Select, Switch,
    TextField, Tooltip, Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import AsyncFormDialog from '../../lib/AsyncFormDialog';

// MQTT enum constants — local copy of wcibapi/libs/mqttEnums.js. Kept
// in sync with that file by lib/mqttEnums.contract.test.js so a drift
// will fail in CI before it can land in production. Update both at once.
const MQTT_LOG_LEVELS = ['error', 'warning', 'notice', 'information', 'debug'];
const INGESTER_LOG_LEVELS = ['DEBUG', 'INFO', 'WARNING', 'ERROR'];
const DATA_FLAGS = ['raw', 'processed'];
const CERT_TYPES = ['letsencrypt', 'selfsigned'];

// Number-input coercion: if the field is empty or non-numeric, return
// undefined so the PATCH handler's `if (val !== undefined && val !== stored)`
// guard skips it. Without this, an empty input becomes NaN → JSON-serialised
// as null → Helm renders the literal `null` and the broker config breaks.
const numericOrUndefined = (raw) => {
    if (raw === '' || raw === null || raw === undefined) return undefined;
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
};

const DEFAULTS = {
    mqtt: { version: '2.0.18', retention: 86400, logLevel: 'warning' },
    ingester: {
        enabled: true,
        filenamePrefix: 'mqtt_ingest',
        dataFlag: 'raw',
        maxDuration: 86400,
        maxSize: 209715200,
        logLevel: 'INFO'
    },
    ingress: { enabled: false, hostname: '', certType: 'letsencrypt' }
};

/**
 * Build initial form state.
 *
 * In CREATE mode, fall back to DEFAULTS for everything the user hasn't
 * supplied — the server uses the same defaults, but pre-filling them gives
 * the user something to anchor on.
 *
 * In EDIT mode, the API returns the actually-running config (the server
 * was patched in F1 to expose filenamePrefix and dataFlag). Use real
 * values verbatim — never let a hardcoded DEFAULT shadow what the running
 * broker is actually using. Anything missing from the response stays
 * empty so the user can see the gap, and an undefined send-back lets the
 * PATCH handler skip those fields. (DES10)
 */
function makeInitialState(existing, mode) {
    if (mode === 'edit' && existing) {
        return {
            mqtt: { ...(existing.mqtt || {}) },
            ingester: { ...(existing.ingester || {}) },
            ingress: { ...(existing.ingress || {}) }
        };
    }
    return {
        mqtt: { ...DEFAULTS.mqtt, ...(existing?.mqtt || {}) },
        ingester: { ...DEFAULTS.ingester, ...(existing?.ingester || {}) },
        ingress: { ...DEFAULTS.ingress, ...(existing?.ingress || {}) }
    };
}

/**
 * Reusable MQTT setup dialog supporting both create and edit modes.
 *
 * Visual language matches MyJWTs: minimal essentials up front, an "Advanced"
 * collapse for the rest. All optional fields fall back to backend defaults.
 *
 * Built on AsyncFormDialog so submit/error/dispatch-lock plumbing is shared
 * with CreateDatasetDialog and ConfirmActionDialog.
 *
 * Props:
 * - open: boolean
 * - mode: 'create' | 'edit'
 * - datasetName: string (display)
 * - existing: optional sanitized config object (for edit mode)
 * - onSubmit(body): async — called with the patch/create body
 * - onSuccess?: () => void — fired after a successful submit
 * - onClose(): void
 */
export default function MqttSetupDialog({ open, mode, datasetName, existing, helmStatus, onSubmit, onSuccess, onClose }) {
    const [form, setForm] = useState(() => makeInitialState(existing, mode));
    const [advancedOpen, setAdvancedOpen] = useState(false);
    // Full-screen on phones — the form has many fields and the title can
    // wrap badly inside a 600px sm-bounded modal on a 360px viewport.
    // (DES25)
    const fullScreen = useMediaQuery(useTheme().breakpoints.down('sm'));

    // Re-seed form whenever the dialog re-opens or the existing config changes.
    useEffect(() => {
        if (open) {
            setForm(makeInitialState(existing, mode));
            setAdvancedOpen(false);
        }
    }, [open, existing, mode]);

    const setField = (section, field, value) => {
        setForm(prev => ({
            ...prev,
            [section]: { ...prev[section], [field]: value }
        }));
    };

    const isCreate = mode === 'create';

    const filenamePrefixError = useMemo(() => {
        if (!form.ingester.enabled) return '';
        if (!form.ingester.filenamePrefix || !form.ingester.filenamePrefix.trim()) {
            return 'Required when ingester is enabled';
        }
        return '';
    }, [form.ingester.enabled, form.ingester.filenamePrefix]);

    const submitBody = () => ({
        mqtt: { ...form.mqtt },
        ingester: { ...form.ingester },
        ingress: { ...form.ingress }
    });

    return (
        <AsyncFormDialog
            open={open}
            onClose={onClose}
            title={isCreate ? `Set up MQTT for ${datasetName}` : `Edit MQTT setup for ${datasetName}`}
            subtitle={!isCreate && helmStatus
                ? `Currently deployed — Helm status: ${helmStatus}`
                : undefined}
            submitLabel={isCreate ? 'Set up MQTT' : 'Save changes'}
            submitIcon={<SaveIcon />}
            canSubmit={!filenamePrefixError}
            onSubmit={() => onSubmit(submitBody())}
            onSuccess={onSuccess}
            dialogProps={{ fullWidth: true, maxWidth: 'sm', fullScreen }}
            >
            <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
                {isCreate
                    ? 'Deploys a Mosquitto broker and an ingester pod for live data uploads. Sensible defaults are pre-filled — open Advanced if you need to tune them.'
                    : 'Edit the running MQTT configuration. Only changed fields are applied.'}
            </Typography>

            {/* Essentials */}
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Ingester</Typography>
            <FormControlLabel
                control={
                    <Switch
                        checked={!!form.ingester.enabled}
                        onChange={(e) => setField('ingester', 'enabled', e.target.checked)}
                    />
                }
                label="Ingester enabled (auto-uploads MQTT data as files)"
            />
            <TextField
                label="Filename prefix"
                fullWidth
                margin="dense"
                size="small"
                value={form.ingester.filenamePrefix || ''}
                onChange={(e) => setField('ingester', 'filenamePrefix', e.target.value)}
                helperText={filenamePrefixError || 'Prefix for files uploaded by the ingester (e.g. "mqtt_ingest")'}
                error={!!filenamePrefixError}
                disabled={!form.ingester.enabled}
            />
            <FormControl fullWidth margin="dense" size="small" disabled={!form.ingester.enabled}>
                <InputLabel id="mqtt-data-flag-label">Data flag</InputLabel>
                <Select
                    labelId="mqtt-data-flag-label"
                    label="Data flag"
                    value={form.ingester.dataFlag || 'raw'}
                    onChange={(e) => setField('ingester', 'dataFlag', e.target.value)}
                >
                    {DATA_FLAGS.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                </Select>
            </FormControl>

            {/* Advanced */}
            <Card variant="outlined" sx={{ mt: 2 }}>
                <CardActionArea onClick={() => setAdvancedOpen(o => !o)}>
                    <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5 }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2">Advanced</Typography>
                            <Typography variant="caption" color="text.secondary">
                                Defaults are sensible — only open if you need to tune broker version, retention, log levels, ingest limits, or expose the broker publicly.
                            </Typography>
                        </Box>
                        <ExpandMoreIcon sx={{
                            transform: advancedOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s'
                        }} />
                    </Box>
                </CardActionArea>
                <Collapse in={advancedOpen} timeout={200}>
                    <CardContent>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Broker</Typography>
                        <TextField
                            label="Mosquitto version"
                            fullWidth margin="dense" size="small"
                            value={form.mqtt.version}
                            onChange={(e) => setField('mqtt', 'version', e.target.value)}
                        />
                        <TextField
                            label="Retention (seconds)"
                            fullWidth margin="dense" size="small"
                            type="number"
                            value={form.mqtt.retention}
                            onChange={(e) => setField('mqtt', 'retention', numericOrUndefined(e.target.value))}
                        />
                        <FormControl fullWidth margin="dense" size="small">
                            <InputLabel id="mqtt-log-level-label">Broker log level</InputLabel>
                            <Select
                                labelId="mqtt-log-level-label"
                                label="Broker log level"
                                value={form.mqtt.logLevel}
                                onChange={(e) => setField('mqtt', 'logLevel', e.target.value)}
                            >
                                {MQTT_LOG_LEVELS.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                            </Select>
                        </FormControl>

                        <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>Ingester</Typography>
                        <TextField
                            label="Max duration before upload (seconds)"
                            fullWidth margin="dense" size="small"
                            type="number"
                            value={form.ingester.maxDuration}
                            onChange={(e) => setField('ingester', 'maxDuration', numericOrUndefined(e.target.value))}
                            disabled={!form.ingester.enabled}
                        />
                        <TextField
                            label="Max size before upload (bytes)"
                            fullWidth margin="dense" size="small"
                            type="number"
                            value={form.ingester.maxSize}
                            onChange={(e) => setField('ingester', 'maxSize', numericOrUndefined(e.target.value))}
                            disabled={!form.ingester.enabled}
                        />
                        <FormControl fullWidth margin="dense" size="small" disabled={!form.ingester.enabled}>
                            <InputLabel id="ingester-log-level-label">Ingester log level</InputLabel>
                            <Select
                                labelId="ingester-log-level-label"
                                label="Ingester log level"
                                value={form.ingester.logLevel}
                                onChange={(e) => setField('ingester', 'logLevel', e.target.value)}
                            >
                                {INGESTER_LOG_LEVELS.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                            </Select>
                        </FormControl>

                        <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>Ingress</Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!!form.ingress.enabled}
                                    onChange={(e) => setField('ingress', 'enabled', e.target.checked)}
                                />
                            }
                            label="Public WSS ingress"
                        />
                        <Tooltip title="Defaults to mqtt-ds<id>.<MQTT_DOMAIN> when left blank.">
                            <TextField
                                label="Ingress hostname (optional)"
                                fullWidth margin="dense" size="small"
                                value={form.ingress.hostname || ''}
                                onChange={(e) => setField('ingress', 'hostname', e.target.value)}
                                disabled={!form.ingress.enabled}
                            />
                        </Tooltip>
                        <FormControl fullWidth margin="dense" size="small" disabled={!form.ingress.enabled}>
                            <InputLabel id="ingress-cert-type-label">TLS certificate type</InputLabel>
                            <Select
                                labelId="ingress-cert-type-label"
                                label="TLS certificate type"
                                value={form.ingress.certType || 'letsencrypt'}
                                onChange={(e) => setField('ingress', 'certType', e.target.value)}
                            >
                                {CERT_TYPES.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </CardContent>
                </Collapse>
            </Card>
        </AsyncFormDialog>
    );
}

MqttSetupDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    mode: PropTypes.oneOf(['create', 'edit']).isRequired,
    datasetName: PropTypes.string.isRequired,
    existing: PropTypes.object,
    helmStatus: PropTypes.string,
    onSubmit: PropTypes.func.isRequired,
    onSuccess: PropTypes.func,
    onClose: PropTypes.func.isRequired
};

// Exported for the contract test in lib/mqttEnums.contract.test.js — must
// match the server-side definitions in wcibapi/config/mqttEnums.js.
export const __ENUMS__ = {
    MQTT_LOG_LEVELS,
    INGESTER_LOG_LEVELS,
    DATA_FLAGS,
    CERT_TYPES
};
